package co.maytes.standupbot.dashboard.components.feedback

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.motion.Presence
import co.maytes.standupbot.dashboard.components.motion.presenceClasses
import co.maytes.standupbot.dashboard.stores.Toast
import co.maytes.standupbot.dashboard.stores.ToastKind
import co.maytes.standupbot.dashboard.stores.rememberUiStore
import co.maytes.standupbot.dashboard.styles.Motion
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Radius
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.dashboard.styles.ZIndex
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.bottom
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.left
import com.varabyte.kobweb.compose.ui.modifiers.onFocusIn
import com.varabyte.kobweb.compose.ui.modifiers.onFocusOut
import com.varabyte.kobweb.compose.ui.modifiers.onMouseEnter
import com.varabyte.kobweb.compose.ui.modifiers.onMouseLeave
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.compose.ui.modifiers.zIndex
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.browser.window
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.percent

private const val TOAST_DURATION_MS = 4_000L

@Composable
fun ToastOverlay() {
    val uiStore = rememberUiStore()
    val toast = uiStore.currentToast
    var hovered by remember(toast?.id) { mutableStateOf(false) }
    var focused by remember(toast?.id) { mutableStateOf(false) }

    AutoDismiss(
        toast = toast,
        paused = hovered || focused,
        onDismiss = uiStore::clearToast,
    )

    val lastToast = remember { LastToast() }
    if (toast != null) lastToast.value = toast

    lastToast.value?.let { shown ->
        key(shown.id) {
            Presence(visible = toast != null, enterMs = Motion.TOAST_IN_MS, exitMs = Motion.TOAST_OUT_MS) { presence ->
                Box(
                    modifier = Modifier
                        .position(Position.Fixed)
                        .bottom(Spacing.xl)
                        .left(50.percent)
                        .zIndex(ZIndex.toast)
                        .backgroundColor(shown.toBackground())
                        .color(Color.rgb(0xFFFFFF))
                        .padding(topBottom = Spacing.sm, leftRight = Spacing.lg)
                        .borderRadius(Radius.md)
                        .presenceClasses("sb-toast", presence)
                        .onMouseEnter { hovered = true }
                        .onMouseLeave { hovered = false }
                        .onFocusIn { focused = true }
                        .onFocusOut { focused = false },
                ) {
                    SpanText(
                        text = shown.message,
                        modifier = Modifier.fontSize(Typography.body.size),
                    )
                }
            }
        }
    }
}

private class LastToast(var value: Toast? = null)

@Composable
private fun AutoDismiss(toast: Toast?, paused: Boolean, onDismiss: () -> Unit) {
    var remainingMs by remember(toast?.id) { mutableStateOf(TOAST_DURATION_MS) }

    LaunchedEffect(toast?.id, paused) {
        if (toast == null || paused) return@LaunchedEffect
        val startedAt = window.performance.now()
        try {
            delay(remainingMs)
            onDismiss()
        } finally {
            remainingMs = (remainingMs - (window.performance.now() - startedAt).toLong()).coerceAtLeast(0L)
        }
    }
}

private fun Toast.toBackground() = when (kind) {
    ToastKind.Success -> Palette.success
    ToastKind.Error -> Palette.error
    ToastKind.Info -> Palette.accent
}
